import { DialogCloseEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const GALLERY_SELECTOR = 'atc-media-gallery';
const DEFERRED_MEDIA_SELECTOR = 'deferred-media';
const PLAYER_SELECTOR = 'iframe[data-video-type], video';

class AtcMediaVideoPlaybackController {
  /** @type {WeakSet<HTMLIFrameElement>} */
  #registeredIframes = new WeakSet();

  /** @type {MutationObserver} */
  #observer;

  constructor() {
    this.#observer = new MutationObserver(this.#handleMutation);
    this.#observer.observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying);
    window.addEventListener('message', this.#handlePlayerMessage);
    window.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    this.#registerPlayers(document);
  }

  /**
   * @param {Event} event
   */
  #handleMediaStartedPlaying = (event) => {
    if (!isMediaStartedPlayingEvent(event)) return;

    const currentMedia = event.detail.resource.closest(DEFERRED_MEDIA_SELECTOR);
    if (!(currentMedia instanceof HTMLElement)) return;

    this.#registerPlayers(currentMedia);
    this.#resetSiblingMedia(currentMedia);
  };

  /**
   * @param {MessageEvent} event
   */
  #handlePlayerMessage = (event) => {
    const iframe = this.#getIframeForMessageSource(event.source);
    if (!iframe) return;
    if (!isPlayerPlayMessage(event.data, iframe.dataset.videoType)) return;

    const currentMedia = iframe.closest(DEFERRED_MEDIA_SELECTOR);
    if (!(currentMedia instanceof HTMLElement)) return;

    this.#resetSiblingMedia(currentMedia);
  };

  #handleDialogClose = () => {
    for (const media of document.querySelectorAll(`${GALLERY_SELECTOR} zoom-dialog ${DEFERRED_MEDIA_SELECTOR}`)) {
      if (media instanceof HTMLElement) {
        resetDeferredMedia(media);
      }
    }
  };

  /**
   * @param {MutationRecord[]} mutations
   */
  #handleMutation = (mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          this.#registerPlayers(node);
        }
      }
    }
  };

  /**
   * @param {ParentNode} root
   */
  #registerPlayers(root) {
    for (const iframe of root.querySelectorAll('iframe[data-video-type]')) {
      if (iframe instanceof HTMLIFrameElement) {
        this.#registerIframe(iframe);
      }
    }
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #registerIframe(iframe) {
    if (this.#registeredIframes.has(iframe)) return;

    this.#registeredIframes.add(iframe);

    if (iframe.dataset.videoType === 'vimeo') {
      const registerVimeoPlayListener = () => {
        iframe.contentWindow?.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), '*');
      };

      iframe.addEventListener('load', registerVimeoPlayListener, { once: true });
      registerVimeoPlayListener();
    }
  }

  /**
   * @param {MessageEventSource | null} source
   * @returns {HTMLIFrameElement | null}
   */
  #getIframeForMessageSource(source) {
    if (!source) return null;

    for (const iframe of document.querySelectorAll(`${GALLERY_SELECTOR} iframe[data-video-type]`)) {
      if (iframe instanceof HTMLIFrameElement && iframe.contentWindow === source) {
        return iframe;
      }
    }

    return null;
  }

  /**
   * @param {HTMLElement} currentMedia
   */
  #resetSiblingMedia(currentMedia) {
    const gallery = currentMedia.closest(GALLERY_SELECTOR);
    if (!gallery) return;

    for (const media of gallery.querySelectorAll(DEFERRED_MEDIA_SELECTOR)) {
      if (media instanceof HTMLElement && media !== currentMedia) {
        resetDeferredMedia(media);
      }
    }
  }
}

/**
 * @param {HTMLElement} media
 */
const resetDeferredMedia = (media) => {
  const player = media.querySelector(PLAYER_SELECTOR);

  if (!player) return;

  if (typeof media.pauseMedia === 'function') {
    media.pauseMedia();
  }

  if (player instanceof HTMLIFrameElement) {
    resetIframePlayer(player);
  } else if (player instanceof HTMLVideoElement) {
    player.pause();
    player.currentTime = 0;
  }

  if ('isPlaying' in media) {
    media.isPlaying = false;
  }

  if (typeof media.updatePlayPauseHint === 'function') {
    media.updatePlayPauseHint(false);
  }
};

/**
 * @param {HTMLIFrameElement} iframe
 */
const resetIframePlayer = (iframe) => {
  if (iframe.dataset.videoType === 'youtube') {
    iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
    iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'seekTo', args: [0, true] }), '*');
  } else if (iframe.dataset.videoType === 'vimeo') {
    iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    iframe.contentWindow?.postMessage(JSON.stringify({ method: 'setCurrentTime', value: 0 }), '*');
  }
};

/**
 * @param {Event} event
 * @returns {event is Event & { detail: { resource: HTMLElement } }}
 */
const isMediaStartedPlayingEvent = (event) =>
  'detail' in event &&
  event.detail !== null &&
  typeof event.detail === 'object' &&
  'resource' in event.detail &&
  event.detail.resource instanceof HTMLElement;

/**
 * @param {unknown} message
 * @param {string | undefined} videoType
 * @returns {boolean}
 */
const isPlayerPlayMessage = (message, videoType) => {
  const data = parseMessageData(message);
  if (!data) return false;

  if (videoType === 'youtube') {
    return (
      (data.event === 'infoDelivery' && data.info?.playerState === 1) ||
      (data.event === 'onStateChange' && data.info === 1)
    );
  }

  if (videoType === 'vimeo') {
    return data.event === 'play';
  }

  return false;
};

/**
 * @param {unknown} message
 * @returns {Record<string, any> | null}
 */
const parseMessageData = (message) => {
  if (typeof message === 'string') {
    try {
      return JSON.parse(message);
    } catch (_error) {
      return null;
    }
  }

  if (message && typeof message === 'object') {
    return /** @type {Record<string, any>} */ (message);
  }

  return null;
};

if (!window.atcMediaVideoPlaybackController) {
  window.atcMediaVideoPlaybackController = new AtcMediaVideoPlaybackController();
}
